import { useState, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import {
  IdCard, MapPin, Calendar, Clock, Phone, Users, FileText,
  Car, Loader2, ArrowRight, CheckCircle2, AlertCircle, Navigation,
  CircleDot, PlayCircle, Flag, TrendingUp, Wallet, CalendarClock
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEP_LABELS = {
  en_route: { label: 'En route', icon: Navigation, color: 'text-blue-400 bg-blue-500/10 border-blue-500/30' },
  arrived: { label: 'Sur place', icon: CircleDot, color: 'text-amber-300 bg-amber-500/10 border-amber-500/30' },
  started: { label: 'Course démarrée', icon: PlayCircle, color: 'text-emerald-300 bg-emerald-500/10 border-emerald-500/30' },
  completed: { label: 'Terminée', icon: Flag, color: 'text-zinc-400 bg-zinc-700/30 border-zinc-600' },
};

const NEXT_STEP = (current) => {
  if (!current) return 'en_route';
  if (current === 'en_route') return 'arrived';
  if (current === 'arrived') return 'started';
  if (current === 'started') return 'completed';
  return null;
};

export const DriverPortalPage = () => {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState({});

  const fetchData = useCallback(async () => {
    try {
      const [home, st] = await Promise.all([
        axios.get(`${API}/driver-portal/${token}`),
        axios.get(`${API}/driver-portal/${token}/stats`).catch(() => ({ data: null })),
      ]);
      setData(home.data);
      setStats(st.data);
      setError(null);
    } catch (e) {
      setError(e.response?.data?.detail || 'Lien invalide ou expiré');
    } finally { setLoading(false); }
  }, [token]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const advanceStep = async (bookingId, step) => {
    setActionLoading(p => ({ ...p, [bookingId]: true }));
    try {
      await axios.post(`${API}/driver-portal/${token}/ride/${bookingId}/step?step=${step}`);
      toast.success(`Étape « ${STEP_LABELS[step]?.label || step} » enregistrée`);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Erreur');
    } finally {
      setActionLoading(p => ({ ...p, [bookingId]: false }));
    }
  };

  const downloadBdc = async (bookingId) => {
    try {
      const res = await axios.get(
        `${API}/driver-portal/${token}/booking/${bookingId}/bon-de-commande`,
        { responseType: 'blob' }
      );
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `bon-commande-${bookingId.slice(0, 8)}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      toast.error('Téléchargement impossible');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center">
        <Loader2 className="w-10 h-10 text-emerald-500 animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#09090b] flex items-center justify-center p-6">
        <div className="glass-card rounded-2xl p-8 max-w-md text-center" data-testid="driver-portal-error">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white mb-2">Accès impossible</h1>
          <p className="text-zinc-400 text-sm">{error}</p>
          <p className="text-zinc-500 text-xs mt-4">Contactez l'administrateur pour un nouveau lien.</p>
        </div>
      </div>
    );
  }

  const { driver, upcoming = [], past = [] } = data;

  return (
    <div className="min-h-screen bg-[#09090b]">
      {/* Header */}
      <header className="sticky top-0 z-40 bg-gradient-to-b from-emerald-500/10 to-transparent backdrop-blur-md border-b border-emerald-500/20">
        <div className="max-w-3xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-youmi.png" alt="" className="w-9 h-9" />
            <div>
              <p className="text-[10px] text-emerald-400 uppercase tracking-wider font-semibold">Portail Chauffeur</p>
              <p className="text-white font-bold text-sm font-['Outfit']" data-testid="driver-portal-name">
                {driver.first_name} {driver.last_name}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 px-3 py-1.5 bg-emerald-500/10 border border-emerald-500/30 rounded-lg">
            <IdCard className="w-4 h-4 text-emerald-300" />
            <span className="text-emerald-300 font-mono text-xs font-bold" data-testid="driver-portal-plate">
              {driver.license_plate}
            </span>
          </div>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-4 py-6 space-y-8">
        {/* Financial stats */}
        {stats && (
          <section data-testid="driver-portal-stats">
            <div className="glass-card rounded-2xl p-5 bg-gradient-to-br from-emerald-500/10 via-zinc-900/40 to-amber-500/5 border border-emerald-500/20">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-base font-bold text-emerald-300 flex items-center gap-2 font-['Outfit']">
                  <TrendingUp className="w-4 h-4" /> Vos gains
                </h2>
                <span className="text-[10px] text-zinc-500 uppercase tracking-wider">{stats.month_label}</span>
              </div>
              <div className="grid grid-cols-3 gap-2 mb-3">
                <div className="text-center p-2 rounded-lg bg-zinc-900/60" data-testid="stat-month">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Ce mois</p>
                  <p className="text-emerald-400 text-2xl font-bold font-['Outfit']">
                    {stats.month?.net_total?.toFixed(2) || '0.00'}€
                  </p>
                  <p className="text-zinc-400 text-xs">{stats.month?.count || 0} course{(stats.month?.count || 0) > 1 ? 's' : ''}</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-zinc-900/60" data-testid="stat-week">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Cette semaine</p>
                  <p className="text-emerald-400 text-xl font-bold font-['Outfit']">
                    {stats.week?.net_total?.toFixed(2) || '0.00'}€
                  </p>
                  <p className="text-zinc-400 text-xs">{stats.week?.count || 0} course{(stats.week?.count || 0) > 1 ? 's' : ''}</p>
                </div>
                <div className="text-center p-2 rounded-lg bg-zinc-900/60" data-testid="stat-today">
                  <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Aujourd'hui</p>
                  <p className="text-amber-300 text-xl font-bold font-['Outfit']">
                    {stats.today?.net_total?.toFixed(2) || '0.00'}€
                  </p>
                  <p className="text-zinc-400 text-xs">{stats.today?.count || 0} course{(stats.today?.count || 0) > 1 ? 's' : ''}</p>
                </div>
              </div>
              <div className="flex items-center justify-between text-xs pt-3 border-t border-zinc-800">
                <span className="text-zinc-500 flex items-center gap-1">
                  <Wallet className="w-3 h-3" /> Total : <b className="text-zinc-300">{stats.all_time?.net_total?.toFixed(2) || '0.00'}€</b>
                  <span className="text-zinc-600 ml-1">({stats.all_time?.count || 0})</span>
                </span>
                {stats.next_ride_date && (
                  <span className="text-zinc-400 flex items-center gap-1" data-testid="stat-next-ride">
                    <CalendarClock className="w-3 h-3 text-emerald-400" />
                    Prochaine : <b className="text-emerald-300">{stats.next_ride_date}{stats.next_ride_time ? ` ${stats.next_ride_time}` : ''}</b>
                  </span>
                )}
              </div>
            </div>
          </section>
        )}

        {/* Upcoming rides */}
        <section data-testid="driver-portal-upcoming">
          <h2 className="text-lg font-bold text-white mb-3 flex items-center gap-2 font-['Outfit']">
            <Car className="w-5 h-5 text-emerald-400" /> Courses à venir
            <span className="ml-auto text-xs text-zinc-400 font-normal">{upcoming.length} course{upcoming.length > 1 ? 's' : ''}</span>
          </h2>

          {upcoming.length === 0 ? (
            <div className="glass-card rounded-2xl p-6 text-center text-zinc-500 text-sm">
              Aucune course attribuée pour l'instant. Vous serez notifié dès qu'une course vous est confiée.
            </div>
          ) : (
            <div className="space-y-4">
              {upcoming.map(ride => {
                const currentStep = ride.ride?.step || null;
                const nextStep = NEXT_STEP(currentStep);
                const stepInfo = currentStep ? STEP_LABELS[currentStep] : null;
                return (
                  <div key={ride.id} className="glass-card rounded-2xl p-5 border border-zinc-800 hover:border-emerald-500/40 transition" data-testid={`ride-card-${ride.id}`}>
                    {/* Status pill */}
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2 text-emerald-400 text-sm font-semibold">
                        <Calendar className="w-4 h-4" />
                        {ride.pickup_date} <span className="text-zinc-500">•</span> {ride.pickup_time}
                      </div>
                      {stepInfo ? (
                        <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full border text-[11px] font-semibold ${stepInfo.color}`}>
                          <stepInfo.icon className="w-3 h-3" /> {stepInfo.label}
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2.5 py-1 rounded-full border border-zinc-700 text-zinc-400 text-[11px] font-semibold">
                          À démarrer
                        </span>
                      )}
                    </div>

                    {/* Trip */}
                    <div className="space-y-2 mb-4">
                      <div className="flex items-start gap-2">
                        <div className="mt-1.5 w-2 h-2 rounded-full bg-emerald-400 shrink-0" />
                        <p className="text-white text-sm">{ride.pickup_address}</p>
                      </div>
                      {(ride.stops || []).map((s, i) => (
                        <div key={i} className="flex items-start gap-2 ml-1">
                          <div className="mt-1.5 w-1.5 h-1.5 rounded-full bg-zinc-500 shrink-0" />
                          <p className="text-zinc-400 text-sm">Arrêt : {s.address || s}</p>
                        </div>
                      ))}
                      <div className="flex items-start gap-2">
                        <div className="mt-1.5 w-2 h-2 rounded-full bg-red-400 shrink-0" />
                        <p className="text-white text-sm">{ride.destination_address}</p>
                      </div>
                    </div>

                    {/* Client + price */}
                    <div className="grid grid-cols-2 gap-3 p-3 bg-zinc-900/60 rounded-lg mb-4">
                      <div>
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Client</p>
                        <p className="text-white text-sm font-medium">{ride.first_name} {ride.last_name}</p>
                        {ride.phone && (
                          <a href={`tel:${ride.phone}`} className="text-emerald-400 text-xs flex items-center gap-1 mt-0.5">
                            <Phone className="w-3 h-3" /> {ride.phone}
                          </a>
                        )}
                        {ride.passengers > 1 && (
                          <p className="text-zinc-500 text-xs flex items-center gap-1 mt-0.5">
                            <Users className="w-3 h-3" /> {ride.passengers} passagers
                          </p>
                        )}
                      </div>
                      <div className="text-right">
                        <p className="text-[10px] text-zinc-500 uppercase tracking-wider">Prix net chauffeur</p>
                        <p className="text-emerald-400 text-2xl font-bold font-['Outfit']" data-testid={`ride-net-price-${ride.id}`}>
                          {ride.driver_net_price ? `${ride.driver_net_price}€` : '—'}
                        </p>
                      </div>
                    </div>

                    {/* Notes */}
                    {ride.notes && (
                      <p className="text-amber-300/80 text-xs bg-amber-500/5 border border-amber-500/20 rounded-lg p-2 mb-3">
                        Note : {ride.notes}
                      </p>
                    )}

                    {/* Actions */}
                    <div className="flex flex-wrap gap-2">
                      {nextStep && currentStep !== 'completed' && (
                        <Button
                          onClick={() => advanceStep(ride.id, nextStep)}
                          disabled={actionLoading[ride.id]}
                          className="bg-emerald-600 hover:bg-emerald-700 text-white text-sm flex-1"
                          data-testid={`next-step-${ride.id}`}
                        >
                          {actionLoading[ride.id] ? <Loader2 className="w-4 h-4 animate-spin" /> : (
                            <>
                              {STEP_LABELS[nextStep]?.label} <ArrowRight className="w-4 h-4 ml-1" />
                            </>
                          )}
                        </Button>
                      )}
                      <Button
                        variant="outline"
                        onClick={() => downloadBdc(ride.id)}
                        className="border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 text-sm"
                        data-testid={`download-bdc-${ride.id}`}
                      >
                        <FileText className="w-4 h-4 mr-1" /> Bon de réservation
                      </Button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </section>

        {/* Past rides */}
        {past.length > 0 && (
          <section data-testid="driver-portal-past">
            <h2 className="text-base font-bold text-zinc-400 mb-3 flex items-center gap-2">
              <CheckCircle2 className="w-4 h-4" /> Historique récent
            </h2>
            <div className="space-y-2">
              {past.slice(0, 10).map(ride => (
                <div key={ride.id} className="bg-zinc-900/50 rounded-lg p-3 border border-zinc-800 text-sm" data-testid={`past-ride-${ride.id}`}>
                  <div className="flex items-center justify-between">
                    <div className="min-w-0 flex-1">
                      <p className="text-zinc-300 truncate">{ride.pickup_address?.split(',')[0]} → {ride.destination_address?.split(',')[0]}</p>
                      <p className="text-zinc-500 text-xs">{ride.pickup_date} {ride.pickup_time} • {ride.first_name} {ride.last_name}</p>
                    </div>
                    <div className="text-emerald-400 font-bold text-sm shrink-0 ml-3">
                      {ride.driver_net_price ? `${ride.driver_net_price}€` : '—'}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}

        <p className="text-center text-xs text-zinc-600 pt-6 flex items-center justify-center gap-1">
          <Clock className="w-3 h-3" /> Mis à jour à l'instant
        </p>
      </main>
    </div>
  );
};

export default DriverPortalPage;
